import { ClockIcon, SlidersHorizontalIcon, SparklesIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  Button,
  Card,
  CardContent,
  CardHeading,
  SectionHeading,
} from '@/components/ui'
import {
  type Analysis,
  type AnalysisPayload,
  useReportConfigOptions,
  useReportShapePayload,
  useWizardActions,
} from '@/modules/analysis'
import { DEFAULT_CASE_TYPE, type Project } from '@/modules/projects'
import { CaseSummary } from './CaseSummary'
import { CustomizeReport } from './CustomizeReport'

interface GenerateReportProps {
  project: Project
  analysis: Analysis
  isSubmitting: boolean
  transactionCount: number
  onSubmit: (payload: AnalysisPayload) => void
}

const estimatedTime = (transactionCount: number): string => {
  if (transactionCount < 1000) return '5-10 minutes'
  if (transactionCount < 5000) return '10-15 minutes'
  if (transactionCount < 10000) return '15-20 minutes'
  return '20-30+ minutes'
}

export default function GenerateReport({
  project,
  analysis,
  isSubmitting,
  transactionCount,
  onSubmit,
}: GenerateReportProps) {
  const [customizing, setCustomizing] = useState(false)
  const config = useReportConfigOptions()
  const reportShapePayload = useReportShapePayload()
  const {
    seedSectionsForCase,
    setSections,
    setSectionsInstructions,
    setInstructions,
    reset,
  } = useWizardActions()

  // Report shape prefills from the last report's metadata when regenerating,
  // else from the case type's suggested sections.
  useEffect(() => {
    if (!config) return

    seedSectionsForCase(project.caseType ?? DEFAULT_CASE_TYPE, config)

    const metadata = analysis?.metadata
    if (metadata?.sections?.length) setSections(metadata.sections)
    if (metadata?.section_instructions) {
      setSectionsInstructions(metadata.section_instructions)
    }
    if (metadata?.instructions) setInstructions(metadata.instructions)

    return () => reset()
  }, [config])

  return (
    <div className="mr-auto space-y-8">
      <SectionHeading
        title="AI forensic analysis"
        description={
          <span className="block max-w-2xl">
            Generate a report from the case details you already provided, or
            customize the sections and instructions first.
          </span>
        }
      />

      {customizing ? (
        <CustomizeReport
          project={project}
          isSubmitting={isSubmitting}
          onGenerate={() => onSubmit(reportShapePayload)}
          onCancel={() => setCustomizing(false)}
        />
      ) : (
        <Card className="max-w-3xl">
          <CardHeading
            title="Generate report"
            description="Uses the recommended setup for this case."
          />

          <CardContent className="space-y-6">
            <CaseSummary />

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => onSubmit(reportShapePayload)}
                isLoading={isSubmitting}
              >
                <SparklesIcon className="h-4 w-4" />
                Generate report
              </Button>
              <Button theme="outline" onClick={() => setCustomizing(true)}>
                <SlidersHorizontalIcon className="h-4 w-4" />
                Customize report
              </Button>
              <span className="flex items-center gap-1.5 text-xs text-ink-subtle">
                <ClockIcon className="h-3.5 w-3.5" />
                Usually takes {estimatedTime(transactionCount)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
